import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, DefinitionList, Dialog, DropdownMenu, Icon, Label, Select, Table, Text, TextArea, TextInput, useToaster } from '@gravity-ui/uikit';
import { ArrowRotateRight, Ban, Clock, Lock, Play, Wallet } from '@gravity-ui/icons';
import type { AdminActionResult, AdminTenantDetail } from '@odonix/contracts';
import { useTenantsApi } from '../../lib/api/context.js';
import { useManagersApi } from '../../lib/api/managers-context.js';
import { TenantsError } from '../../lib/api/tenants.js';
import { Field } from '../../ui/field.js';
import { day, moment, parseRubles, rub } from '../../ui/format.js';
import { Page, Section } from '../../ui/page.js';
import { RouterLink } from '../../ui/router-link.js';
import { ErrorNote, LoadingRows } from '../../ui/states.js';
import { KIND_LABEL, STATE_LABEL, STATE_TONE, paymentKind, paymentStatus } from './labels.js';
import { DateInput } from '../../ui/date-input.js';

/**
 * Карточка клиники: профиль, подписка, менеджер, история платежей и пять
 * действий платформы. Каждое действие — диалог с обязательной причиной:
 * форма без причины не отправляется, это первая линия защиты перед серверной
 * (`Reason` в контрактах).
 */

type DialogKind = 'extend-trial' | 'activate' | 'manual-payment' | 'block' | 'restrict' | 'manager';

export function TenantDetailScreen({ tenantId }: { tenantId: string }) {
  const api = useTenantsApi();
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState<DialogKind | null>(null);

  const query = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => api.detail(tenantId),
  });

  function afterAction(): void {
    setOpenDialog(null);
    void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
    void queryClient.invalidateQueries({ queryKey: ['tenants'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }

  const detail = query.data;
  const close = () => {
    setOpenDialog(null);
  };

  return (
    <Page
      title={detail?.tenant.name ?? 'Клиника'}
      crumbs={[{ label: 'Клиники', to: '/tenants' }]}
      aside={
        detail === undefined ? null : (
          <Label theme={STATE_TONE[detail.tenant.subscriptionState]} size="m">
            {STATE_LABEL[detail.tenant.subscriptionState]}
          </Label>
        )
      }
      actions={
        detail === undefined ? undefined : (
          <>
            <Button
              view="outlined"
              onClick={() => {
                setOpenDialog('extend-trial');
              }}
            >
              <Icon data={Clock} size={16} />
              Продлить триал
            </Button>
            <Button
              view="outlined"
              onClick={() => {
                setOpenDialog('activate');
              }}
            >
              <Icon data={Play} size={16} />
              Активировать
            </Button>
            <Button
              view="action"
              onClick={() => {
                setOpenDialog('manual-payment');
              }}
            >
              <Icon data={Wallet} size={16} />
              Оплата наличными
            </Button>
            <DropdownMenu
              renderSwitcher={(props) => (
                <Button {...props} view="outlined" aria-label="Ограничить доступ">
                  <Icon data={Lock} size={16} />
                  Ограничить
                </Button>
              )}
              items={[
                {
                  text: 'Ограничить запись',
                  iconStart: <Icon data={Lock} size={16} />,
                  action: () => {
                    setOpenDialog('restrict');
                  },
                },
                {
                  text: 'Заблокировать полностью',
                  theme: 'danger',
                  iconStart: <Icon data={Ban} size={16} />,
                  action: () => {
                    setOpenDialog('block');
                  },
                },
              ]}
            />
          </>
        )
      }
    >
      {query.isError ? (
        <ErrorNote title="Карточка не загрузилась" error={query.error} onRetry={() => void query.refetch()} />
      ) : detail === undefined ? (
        <LoadingRows rows={6} />
      ) : (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <ProfileSection detail={detail} />
          <SubscriptionSection detail={detail} />
          <ManagerSection
            detail={detail}
            onChange={() => {
              setOpenDialog('manager');
            }}
          />
          <PaymentsSection detail={detail} />
        </div>
      )}

      {detail === undefined ? null : (
        <>
          <ExtendTrialDialog open={openDialog === 'extend-trial'} tenantId={tenantId} onClose={close} onDone={afterAction} />
          <ReasonDialog
            open={openDialog === 'activate'}
            title="Перевести в активную"
            apply="Активировать"
            note="Снимает блокировку записи, но не продлевает оплаченный период. Если он уже в прошлом или не задан, обычный ночной цикл списания не подхватит эту подписку — для настоящего продления используйте «Оплата наличными»."
            run={(reason) => api.activate(tenantId, { reason })}
            onClose={close}
            onDone={afterAction}
          />
          <ManualPaymentDialog open={openDialog === 'manual-payment'} tenantId={tenantId} onClose={close} onDone={afterAction} />
          <ReasonDialog
            open={openDialog === 'restrict'}
            title="Ограничить запись"
            apply="Ограничить"
            note="Запрещает новую запись и изменения. Карты пациентов, расписание и выгрузка остаются доступны клинике — тот же уровень, что при обычной неоплате."
            run={(reason) => api.restrict(tenantId, { reason })}
            onClose={close}
            onDone={afterAction}
          />
          <ReasonDialog
            open={openDialog === 'block'}
            title="Заблокировать клинику полностью"
            apply="Заблокировать"
            danger
            note="Закрывает доступ полностью, включая чтение и выгрузку данных. Самый строгий уровень — для более мягкого варианта используйте «Ограничить запись»."
            run={(reason) => api.block(tenantId, { reason })}
            onClose={close}
            onDone={afterAction}
          />
          <ManagerDialog
            // Новое открытие — с текущим менеджером, а не с выбором прошлого раза.
            key={openDialog === 'manager' ? 'open' : 'closed'}
            open={openDialog === 'manager'}
            tenantId={tenantId}
            current={detail.manager?.id ?? null}
            onClose={close}
            onDone={afterAction}
          />
        </>
      )}
    </Page>
  );
}

function ProfileSection({ detail }: { detail: AdminTenantDetail }) {
  const { tenant } = detail;
  return (
    <Section title="Профиль">
      <DefinitionList nameMaxWidth={180}>
        <DefinitionList.Item name="Тип">{KIND_LABEL[tenant.kind]}</DefinitionList.Item>
        <DefinitionList.Item name="Владелец">{tenant.ownerName ?? '—'}</DefinitionList.Item>
        <DefinitionList.Item name="Почта" {...(tenant.ownerEmail === null ? {} : { copyText: tenant.ownerEmail })}>
          {tenant.ownerEmail ?? '—'}
        </DefinitionList.Item>
        <DefinitionList.Item name="Телефон" {...(tenant.ownerPhone === null ? {} : { copyText: tenant.ownerPhone })}>
          {tenant.ownerPhone ?? '—'}
        </DefinitionList.Item>
        <DefinitionList.Item name="Регистрация">{day(tenant.createdAt)}</DefinitionList.Item>
      </DefinitionList>
    </Section>
  );
}

function SubscriptionSection({ detail }: { detail: AdminTenantDetail }) {
  const { tenant } = detail;
  return (
    <Section title="Подписка">
      <DefinitionList nameMaxWidth={180}>
        <DefinitionList.Item name="Тариф">{detail.plan.name}</DefinitionList.Item>
        <DefinitionList.Item name="Цена">{detail.plan.priceMinor === 0 ? '—' : rub(detail.plan.priceMinor)}</DefinitionList.Item>
        <DefinitionList.Item name="Пробный период до">
          {tenant.trialEndsOn === null ? '—' : day(tenant.trialEndsOn)}
        </DefinitionList.Item>
        <DefinitionList.Item name="Оплачено до">
          {tenant.currentPeriodEnd === null ? '—' : day(tenant.currentPeriodEnd)}
        </DefinitionList.Item>
        <DefinitionList.Item name="Способ оплаты">
          {detail.method === null
            ? 'Не привязан'
            : `${detail.method.cardType ?? detail.method.title ?? 'Карта'}${detail.method.last4 === null ? '' : ` ···· ${detail.method.last4}`}`}
        </DefinitionList.Item>
        {detail.lastFailure === null ? null : (
          <DefinitionList.Item name="Последний отказ">{detail.lastFailure}</DefinitionList.Item>
        )}
        {detail.cancelReason === null ? null : (
          <DefinitionList.Item name="Причина отмены">{detail.cancelReason}</DefinitionList.Item>
        )}
      </DefinitionList>
    </Section>
  );
}

function ManagerSection({ detail, onChange }: { detail: AdminTenantDetail; onChange: () => void }) {
  const manager = detail.manager;
  return (
    <Section
      title="Менеджер"
      actions={
        <Button view="flat-secondary" onClick={onChange}>
          <Icon data={ArrowRotateRight} size={16} />
          {manager === null ? 'Закрепить' : 'Сменить'}
        </Button>
      }
    >
      {manager === null ? (
        <Text color="secondary">
          Клиника ни за кем не закреплена: процент с её оплат никому не начисляется.
        </Text>
      ) : (
        <DefinitionList nameMaxWidth={180}>
          <DefinitionList.Item name="Ведёт">
            <RouterLink to={`/managers/${manager.id}`}>{manager.fullName}</RouterLink>
          </DefinitionList.Item>
          <DefinitionList.Item name="С">{day(manager.since)}</DefinitionList.Item>
          <DefinitionList.Item name="Как">
            {manager.source === 'referral' ? 'Зарегистрировалась по его ссылке' : 'Закрепил сотрудник платформы'}
          </DefinitionList.Item>
        </DefinitionList>
      )}
    </Section>
  );
}

function PaymentsSection({ detail }: { detail: AdminTenantDetail }) {
  return (
    <Section title="История платежей" flush className="xl:col-span-2">
      {detail.payments.length === 0 ? (
        <div className="px-5 pb-4">
          <Text color="secondary">Платежей ещё не было.</Text>
        </div>
      ) : (
        <div>
          <Table
            className="w-full"
            edgePadding
            stickyHorizontalScroll
            data={detail.payments}
            getRowDescriptor={(row) => ({ id: row.id })}
            columns={[
              {
                id: 'period',
                name: 'Период',
                template: (row) => <span className="tabular">{`${day(row.periodStart)} — ${day(row.periodEnd)}`}</span>,
              },
              { id: 'kind', name: 'Тип', template: (row) => paymentKind(row.kind) },
              {
                id: 'amount',
                name: 'Сумма',
                align: 'end',
                template: (row) => <span className="tabular">{rub(row.amountMinor)}</span>,
              },
              { id: 'status', name: 'Статус', template: (row) => paymentStatus(row.status, row.failureReason) },
              {
                id: 'at',
                name: 'Дата',
                template: (row) => <span className="tabular">{moment(row.settledAt ?? row.createdAt)}</span>,
              },
            ]}
          />
        </div>
      )}
    </Section>
  );
}

// --- Диалоги действий -----------------------------------------------------

function errorText(error: unknown): string | undefined {
  if (error === null || error === undefined) return undefined;
  return error instanceof TenantsError ? error.message : 'Не удалось выполнить действие';
}

function ReasonField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <Field label="Причина" hint="Попадёт в журнал действий и в историю подписки клиники." required>
      {(id) => (
        <TextArea
          id={id}
          value={value}
          onUpdate={onChange}
          minRows={2}
          placeholder="Например: клиника позвонила и попросила продлить"
        />
      )}
    </Field>
  );
}

function ActionDialog({
  open,
  title,
  apply,
  danger = false,
  disabled,
  loading,
  error,
  onApply,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  apply: string;
  danger?: boolean;
  disabled: boolean;
  loading: boolean;
  error: unknown;
  onApply: () => void;
  onClose: () => void;
  children: ReactNode;
}) {
  const message = errorText(error);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="s" fullWidth aria-label={title}>
      <Dialog.Header caption={title} />
      <Dialog.Body>
        <div className="flex flex-col gap-4">{children}</div>
      </Dialog.Body>
      <Dialog.Footer
        textButtonApply={apply}
        textButtonCancel="Отмена"
        onClickButtonApply={onApply}
        onClickButtonCancel={onClose}
        loading={loading}
        propsButtonApply={{ disabled, view: danger ? 'outlined-danger' : 'action' }}
        {...(message === undefined ? {} : { errorText: message, showError: true })}
      />
    </Dialog>
  );
}

interface DialogProps {
  open: boolean;
  tenantId: string;
  onClose: () => void;
  onDone: () => void;
}

const reasonOk = (reason: string) => reason.trim().length >= 3;

function ReasonDialog({
  open,
  title,
  apply,
  note,
  danger = false,
  run,
  onClose,
  onDone,
}: {
  open: boolean;
  title: string;
  apply: string;
  note: string;
  danger?: boolean;
  run: (reason: string) => Promise<AdminActionResult>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const mutation = useMutation<AdminActionResult, unknown>({ mutationFn: () => run(reason.trim()), onSuccess: onDone });

  return (
    <ActionDialog
      open={open}
      title={title}
      apply={apply}
      danger={danger}
      disabled={!reasonOk(reason)}
      loading={mutation.isPending}
      error={mutation.error}
      onApply={() => {
        mutation.mutate();
      }}
      onClose={onClose}
    >
      <Text color="secondary">{note}</Text>
      <ReasonField value={reason} onChange={setReason} />
    </ActionDialog>
  );
}

function ExtendTrialDialog({ open, tenantId, onClose, onDone }: DialogProps) {
  const api = useTenantsApi();
  const [newTrialEndsOn, setNewTrialEndsOn] = useState('');
  const [reason, setReason] = useState('');

  const mutation = useMutation<AdminActionResult, unknown>({
    mutationFn: () => api.extendTrial(tenantId, { newTrialEndsOn, reason: reason.trim() }),
    onSuccess: onDone,
  });

  return (
    <ActionDialog
      open={open}
      title="Продлить пробный период"
      apply="Продлить"
      disabled={newTrialEndsOn === '' || !reasonOk(reason)}
      loading={mutation.isPending}
      error={mutation.error}
      onApply={() => {
        mutation.mutate();
      }}
      onClose={onClose}
    >
      <Field label="Новая дата окончания" required>
        {(id) => <DateInput id={id} value={newTrialEndsOn} onUpdate={setNewTrialEndsOn} />}
      </Field>
      <ReasonField value={reason} onChange={setReason} />
    </ActionDialog>
  );
}

function ManualPaymentDialog({ open, tenantId, onClose, onDone }: DialogProps) {
  const api = useTenantsApi();
  const [rubles, setRubles] = useState('4999');
  const [periodDays, setPeriodDays] = useState('30');
  const [reason, setReason] = useState('');
  const amountMinor = parseRubles(rubles);
  const days = Number(periodDays);

  const mutation = useMutation<AdminActionResult, unknown>({
    mutationFn: () =>
      api.manualPayment(tenantId, { amountMinor: amountMinor ?? 0, periodDays: days, reason: reason.trim() }),
    onSuccess: onDone,
  });

  const daysOk = Number.isInteger(days) && days >= 1 && days <= 366;

  return (
    <ActionDialog
      open={open}
      title="Провести оплату наличными"
      apply="Провести оплату"
      disabled={amountMinor === null || amountMinor <= 0 || !daysOk || !reasonOk(reason)}
      loading={mutation.isPending}
      error={mutation.error}
      onApply={() => {
        mutation.mutate();
      }}
      onClose={onClose}
    >
      <Text color="secondary">Период считается от сегодняшнего дня. Подписка сразу переходит в «Активна».</Text>
      <Field label="Сумма, ₽" required error={amountMinor === null && rubles !== '' ? 'Сумма — число, например 4999,50' : undefined}>
        {(id) => <TextInput id={id} value={rubles} onUpdate={setRubles} controlProps={{ inputMode: 'decimal' }} />}
      </Field>
      <Field label="Период, дней" required error={!daysOk && periodDays !== '' ? 'От 1 до 366 дней' : undefined}>
        {(id) => <TextInput id={id} value={periodDays} onUpdate={setPeriodDays} controlProps={{ inputMode: 'numeric' }} />}
      </Field>
      <ReasonField value={reason} onChange={setReason} />
    </ActionDialog>
  );
}

const NO_MANAGER = '__none';

function ManagerDialog({
  open,
  tenantId,
  current,
  onClose,
  onDone,
}: DialogProps & { current: string | null }) {
  const tenantsApi = useTenantsApi();
  const managersApi = useManagersApi();
  const toaster = useToaster();
  const managers = useQuery({ queryKey: ['managers'], queryFn: () => managersApi.list(), enabled: open });
  const [selected, setSelected] = useState<string>(current ?? NO_MANAGER);

  const mutation = useMutation({
    mutationFn: () => tenantsApi.assignManager(tenantId, selected === NO_MANAGER ? null : selected),
    onSuccess: () => {
      toaster.add({
        name: `tenant-manager-${tenantId}`,
        title: selected === NO_MANAGER ? 'Менеджер снят' : 'Клиника закреплена',
        theme: 'success',
        autoHiding: 4000,
      });
      onDone();
    },
  });

  return (
    <ActionDialog
      open={open}
      title="Менеджер клиники"
      apply="Сохранить"
      disabled={selected === (current ?? NO_MANAGER)}
      loading={mutation.isPending}
      error={mutation.error}
      onApply={() => {
        mutation.mutate();
      }}
      onClose={onClose}
    >
      <Text color="secondary">
        С этого момента процент с оплат клиники начисляется новому менеджеру. Прошлые начисления остаются за прежним.
      </Text>
      <Field label="Менеджер">
        {(id) => (
          <Select
            id={id}
            width="max"
            filterable={(managers.data?.length ?? 0) > 8}
            value={[selected]}
            onUpdate={(values) => {
              setSelected(values[0] ?? NO_MANAGER);
            }}
            options={[
              { value: NO_MANAGER, content: 'Без менеджера' },
              ...(managers.data ?? []).map((manager) => ({
                value: manager.id,
                content: manager.status === 'disabled' ? `${manager.fullName} (отключён)` : manager.fullName,
              })),
            ]}
          />
        )}
      </Field>
    </ActionDialog>
  );
}
